/**
 * Installer "Prices & Quotes" — single combined page.
 *
 * Replaces the previous split between `/installer/material-prices` (read-only
 * catalogue) and `/installer/material-orders` (Quote Builder). One page now
 * does both: browse RRP + your tier price, drop quantities into rows to spin
 * up a quote, save as draft, submit to Ox.
 *
 * URL contract (?brand=… anchors the active brand on every variant):
 *   • no params                  → browse mode
 *   • ?id=ORDER_ID               → edit / view that existing draft
 *   • ?new=1                     → fresh quote (legacy entry-point; same as
 *                                  browse mode but signals intent in URL)
 *
 * Submitted orders are read-only — quantities still display but inputs are
 * disabled and the cart panel shows "Cannot be edited".
 *
 * Locked-state handling: if the installer doesn't have access to the brand
 * (server returns 403 from /api/installer/product-pricing/:brand), we render
 * the existing amber "complete the training to unlock" empty-state instead
 * of the catalogue + cart.
 */
import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Tag, Lock, AlertCircle, ShoppingCart, Plus, Trash2,
  Save, Send, CheckCircle, FileText, Clock, X,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatApiError } from '@/utils/apiError';
import VoucherRail from '@/components/vouchers/VoucherRail';

const BRAND_TABS = [
  { key: 'probound', label: 'ProBound', color: '#2F5E96' },
  { key: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { key: 'prodecor', label: 'ProDecor', color: '#D4A24B', dark: true },
];

const TIER_LABELS = {
  trade: 'Trade',
  approved: 'Approved',
  elite_approved: 'Elite Approved',
};

const STATUS_BADGE = {
  draft: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Draft', icon: FileText },
  submitted: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Submitted', icon: CheckCircle },
  invoiced: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Invoiced', icon: FileText },
  paid: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Paid', icon: CheckCircle },
  payment_confirmed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Payment confirmed', icon: CheckCircle },
  dispatched: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Dispatched', icon: CheckCircle },
  delivered: { bg: 'bg-gray-200', text: 'text-gray-800', label: 'Delivered', icon: CheckCircle },
};

const fmt = (v) => {
  if (v == null) return null;
  const n = Number(v);
  if (!n || n <= 0) return null;
  return `£${n.toFixed(2)}`;
};

const discount = (rrp, mine) => {
  if (!rrp || !mine || rrp <= 0) return null;
  const pct = ((rrp - mine) / rrp) * 100;
  if (pct <= 0 || pct >= 100) return null;
  return pct.toFixed(0);
};

export default function InstallerMaterialPrices() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();

  const editId = searchParams.get('id') || null;
  const initialBrand = searchParams.get('brand') || 'probound';

  const [brand, setBrand] = useState(initialBrand);
  const [products, setProducts] = useState([]);
  const [tier, setTier] = useState({ id: null, label: null, assigned: false });
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  // Quote-building state
  const [qtys, setQtys] = useState({});            // {product_id: "1.5"}
  const [notes, setNotes] = useState('');
  const [customerRef, setCustomerRef] = useState('');
  const [orderId, setOrderId] = useState(editId);
  const [orderStatus, setOrderStatus] = useState('draft');
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);

  // Ambassador credit — only relevant when the installer is dual-role
  // (`user.affiliate_own_id` set). Balance is pulled once at mount so the
  // cart can preview "Apply my credit" without touching the server on
  // every keystroke.
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditToApply, setCreditToApply] = useState('');   // user-entered string
  const [voucher, setVoucher] = useState({ id: null, value: 0 });   // training voucher (one per order)
  const isAmbassador = Boolean(user?.affiliate_own_id);

  // Drafts / history strip
  const [drafts, setDrafts] = useState([]);

  const hasAccess = useCallback(
    (b) => Boolean(user && (user.access || {})[b]),
    [user],
  );

  const isReadOnly = orderStatus === 'submitted';

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadPrices = useCallback(async (b) => {
    setLoading(true);
    setLocked(false);
    try {
      const r = await fetch(`${API_URL}/api/installer/material-prices?brand=${b}`, {
        credentials: 'include',
      });
      if (r.status === 403) {
        setProducts([]);
        setTier({ id: null, label: null, assigned: false });
        setLocked(true);
        return;
      }
      if (!r.ok) {
        const detail = formatApiError(await r.json().catch(() => ({}))) || 'Failed to load prices';
        throw new Error(detail);
      }
      const data = await r.json();
      setProducts(data.products || []);
      setTier({ id: data.tier_id, label: data.tier_label, assigned: data.tier_assigned });
    } catch (e) {
      toast.error(e.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDrafts = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/installer/material-orders`, { credentials: 'include' });
      if (!r.ok) return;
      setDrafts(await r.json());
    } catch {
      // non-blocking
    }
  }, []);

  const loadExisting = useCallback(async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/installer/material-orders/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load order');
      const data = await r.json();
      setBrand(data.brand);
      setNotes(data.notes || '');
      setCustomerRef(data.customer_reference || '');
      setOrderId(id);
      setOrderStatus(data.status);
      const q = {};
      for (const it of (data.items || [])) q[it.product_id] = String(it.quantity);
      setQtys(q);
      setSavedSnapshot({
        qtys: q,
        notes: data.notes || '',
        customerRef: data.customer_reference || '',
      });
    } catch (e) {
      toast.error(e.message);
      navigate('/installer/material-prices', { replace: true });
    }
  }, [navigate]);

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (user && user.role === 'installer') {
      loadPrices(brand);
      loadDrafts();
    }
  }, [brand, loadPrices, loadDrafts, user]);

  useEffect(() => {
    if (editId) loadExisting(editId);
  }, [editId, loadExisting]);

  // Sync URL brand → state when user uses back/forward
  useEffect(() => {
    const urlBrand = searchParams.get('brand');
    if (urlBrand && urlBrand !== brand) setBrand(urlBrand);
  }, [searchParams, brand]);

  // Ambassador credit balance — fetched once when the installer is dual-
  // role. Non-ambassadors never hit this endpoint (`isAmbassador` guard).
  useEffect(() => {
    if (!isAmbassador) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/affiliate/credit-ledger?limit=1`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setCreditBalance(Number(data?.balance) || 0);
      } catch { /* silent — cart still works without credit UI */ }
    })();
    return () => { cancelled = true; };
  }, [isAmbassador]);

  const switchBrand = (b) => {
    // If user is mid-quote, warn before discarding
    if (Object.keys(qtys).length > 0 && !orderId) {
      const ok = window.confirm('You have unsaved items in your quote. Switching brand will clear them. Continue?');
      if (!ok) return;
    }
    setQtys({});
    setOrderId(null);
    setOrderStatus('draft');
    setSavedSnapshot(null);
    setNotes('');
    setCustomerRef('');
    setBrand(b);
    const next = new URLSearchParams(searchParams);
    next.set('brand', b);
    next.delete('id');
    next.delete('new');
    setSearchParams(next, { replace: true });
  };

  const newQuote = () => {
    setQtys({});
    setOrderId(null);
    setOrderStatus('draft');
    setSavedSnapshot(null);
    setNotes('');
    setCustomerRef('');
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    next.set('brand', brand);
    setSearchParams(next, { replace: true });
    toast.info('Started a fresh quote.');
  };

  const openDraft = (row) => {
    if (row.brand !== brand) {
      // Brand switch + draft load in one navigation
      setBrand(row.brand);
    }
    const next = new URLSearchParams();
    next.set('brand', row.brand);
    next.set('id', row.id);
    setSearchParams(next, { replace: false });
  };

  // ── Cart maths ───────────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const g = {};
    for (const p of products) (g[p.category || 'Misc'] ||= []).push(p);
    return g;
  }, [products]);

  const lineTotals = useMemo(() => {
    let subtotal = 0;
    const lines = [];
    for (const [pid, qStr] of Object.entries(qtys)) {
      const qty = parseFloat(qStr);
      if (!qty || qty <= 0) continue;
      const p = products.find((x) => x.id === pid);
      if (!p) continue;
      const line = (p.price || 0) * qty;
      subtotal += line;
      lines.push({ ...p, qty, line });
    }
    return { lines, subtotal: Math.round(subtotal * 100) / 100 };
  }, [qtys, products]);

  const cartActive = lineTotals.lines.length > 0 || !!orderId;
  const hasMissingPrice = lineTotals.lines.some((l) => l.price == null);
  const dirty = useMemo(() => {
    if (!savedSnapshot) return Object.keys(qtys).length > 0 || notes || customerRef;
    const sameQ = JSON.stringify(qtys) === JSON.stringify(savedSnapshot.qtys);
    return !sameQ || notes !== savedSnapshot.notes || customerRef !== savedSnapshot.customerRef;
  }, [qtys, notes, customerRef, savedSnapshot]);

  const setQty = (pid, val) =>
    setQtys((prev) => {
      const next = { ...prev };
      if (val === '' || val === '0') delete next[pid];
      else next[pid] = val;
      return next;
    });

  const itemsPayload = () =>
    Object.entries(qtys)
      .map(([pid, q]) => ({ product_id: pid, quantity: parseFloat(q) }))
      .filter((it) => it.quantity > 0);

  // ── Save / Submit / Delete ───────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      const items = itemsPayload();
      const body = { brand, items, notes, customer_reference: customerRef };
      const res = orderId
        ? await fetch(`${API_URL}/api/installer/material-orders/${orderId}`, {
            method: 'PUT', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
        : await fetch(`${API_URL}/api/installer/material-orders`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      const id = orderId || data.id;
      setOrderId(id);
      setOrderStatus(data.status || 'draft');
      setSavedSnapshot({ qtys: { ...qtys }, notes, customerRef });
      toast.success(orderId ? 'Draft updated.' : 'Draft saved.');
      const next = new URLSearchParams(searchParams);
      next.set('brand', brand);
      next.set('id', id);
      setSearchParams(next, { replace: true });
      loadDrafts();
      return id;
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    // Validate credit input before spending a confirm modal on the user.
    const rawCredit = (creditToApply || '').trim();
    const creditValue = rawCredit === '' ? 0 : parseFloat(rawCredit);
    if (rawCredit !== '' && (Number.isNaN(creditValue) || creditValue < 0)) {
      toast.error('Credit to apply must be a positive number.');
      return;
    }
    if (creditValue > 0) {
      const cap = Math.min(creditBalance, lineTotals.subtotal);
      if (creditValue > cap + 0.005) {
        toast.error(`Credit capped at £${cap.toFixed(2)}.`);
        return;
      }
    }
    const confirmBody = creditValue > 0
      ? `Once submitted, the quote becomes an order in Ox\u2019s queue. £${creditValue.toFixed(2)} of your ambassador credit will be applied. You won\u2019t be able to edit the order afterwards.`
      : 'Once submitted, the quote becomes an order in Ox\u2019s queue and you won\u2019t be able to edit it. Continue?';
    const ok = await confirm({
      title: 'Submit this quote to Ox?',
      body: confirmBody,
      confirmLabel: creditValue > 0 ? `Submit & Apply £${creditValue.toFixed(2)}` : 'Submit to Ox',
      tone: 'success',
    });
    if (!ok) return;
    try {
      // Save first if we have unsaved changes (or haven't saved at all yet).
      let id = orderId;
      if (!id || dirty) {
        id = await save();
        if (!id) return;
      }
      const res = await fetch(`${API_URL}/api/installer/material-orders/${id}/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...(creditValue > 0 ? { credit_to_apply: creditValue } : {}), ...(voucher.id ? { voucher_id: voucher.id } : {}) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      const applied = Number(data?.credit_applied) || 0;
      if (applied > 0) {
        // Optimistically deduct the applied amount from the shown balance.
        setCreditBalance((b) => Math.max(0, b - applied));
        setCreditToApply('');
        toast.success(`Submitted — £${applied.toFixed(2)} credit applied. Ox have been notified.`);
      } else if (Number(data?.voucher_applied) > 0) {
        toast.success(`Submitted — your training voucher saved you £${Number(data.voucher_applied).toFixed(2)}. To pay: £${Number(data.to_pay).toFixed(2)} excl. VAT.`, { duration: 8000 });
      } else {
        toast.success('Submitted — Ox have been notified.');
      }
      setVoucher({ id: null, value: 0 });
      setOrderStatus('submitted');
      loadDrafts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteDraft = async () => {
    if (!orderId) return;
    const ok = await confirm({
      title: 'Delete this draft?',
      body: 'This will discard all line items. You can\u2019t undo.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const r = await fetch(`${API_URL}/api/installer/material-orders/${orderId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error(formatApiError(await r.json()) || 'Failed');
      toast.success('Draft deleted.');
      newQuote();
      loadDrafts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ── Render guards ────────────────────────────────────────────────────────
  if (!user) return null;
  if (user.role !== 'installer') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center">
        <p className="text-sm text-gray-500">This page is for installers only.</p>
      </div>
    );
  }

  const brandConf = BRAND_TABS.find((b) => b.key === brand);
  const tierLabel = tier.id ? tier.label || TIER_LABELS[tier.id] || tier.id : null;
  const brandDrafts = drafts.filter((d) => d.brand === brand);

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <Header />
      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-5">
        {/* Title & status */}
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" style={{ color: brandConf?.color }} />
              {brandConf?.label} — New Order
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
              Enter quantities · save as draft or submit to Ox. Prices are managed by Ox — see the price list on{' '}
              <a href="/staff/pricing-tiers" className="underline text-emerald-700 hover:text-emerald-900">Product Prices</a>.
            </p>
          </div>
          {orderId && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="imp-editing-banner">
              <span className="text-xs text-gray-500">{isReadOnly ? 'Viewing:' : 'Editing:'}</span>
              <StatusPill status={orderStatus} />
              <Button
                size="sm"
                variant="outline"
                onClick={newQuote}
                data-testid="imp-new-quote"
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> Start new quote
              </Button>
            </div>
          )}
        </div>

        {/* Brand picker */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="imp-brand-picker">
          {BRAND_TABS.map((b) => {
            const active = b.key === brand;
            const accessible = hasAccess(b.key);
            return (
              <button
                key={b.key}
                onClick={() => switchBrand(b.key)}
                data-testid={accessible ? `imp-brand-${b.key}` : `imp-brand-${b.key}-locked`}
                aria-disabled={!accessible}
                title={accessible ? `${b.label}` : `Complete the ${b.label} training to unlock pricing`}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold border-2 transition-colors inline-flex items-center gap-1.5 ${
                  active && accessible
                    ? 'border-transparent shadow'
                    : accessible
                      ? 'bg-white text-gray-700 border-gray-200'
                      : 'bg-gray-100 text-gray-400 border-gray-200'
                } ${active && !accessible ? 'ring-2 ring-amber-300' : ''}`}
                style={
                  active && accessible
                    ? { backgroundColor: b.color, color: b.dark ? '#0B0B0E' : '#fff' }
                    : undefined
                }
              >
                {!accessible && <Lock className="w-3 h-3" />}
                {b.label}
              </button>
            );
          })}
        </div>

        {/* Locked state */}
        {locked && (
          <Card className="p-8 sm:p-10 text-center bg-amber-50 border-amber-200" data-testid="imp-locked">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-3">
              <Lock className="w-7 h-7 text-amber-700" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-amber-900 mb-1">
              {brandConf?.label} pricing is locked
            </h3>
            <p className="text-sm text-amber-800 max-w-md mx-auto">
              You haven&apos;t been granted access to {brandConf?.label} yet. Complete the {brandConf?.label} training course (or ask Ox to enable it on your account) to unlock prices and place quotes.
            </p>
            <div className="mt-4 flex gap-2 justify-center">
              <Button onClick={() => navigate('/protraining')} data-testid="imp-locked-cta" className="bg-amber-700 hover:bg-amber-800 text-white">
                View Training Courses
              </Button>
            </div>
          </Card>
        )}

        {/* Drafts strip (only when not locked and there are drafts/history for this brand) */}
        {!locked && brandDrafts.length > 0 && (
          <div className="mb-3" data-testid="imp-drafts-strip">
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
              Your {brandConf?.label} quotes
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {brandDrafts.map((d) => {
                const s = STATUS_BADGE[d.status] || STATUS_BADGE.draft;
                const Icon = s.icon;
                const active = d.id === orderId;
                return (
                  <button
                    key={d.id}
                    onClick={() => openDraft(d)}
                    data-testid={`imp-draft-${d.id}`}
                    className={`shrink-0 px-3 py-2 rounded-lg border text-left transition-colors ${
                      active ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${s.bg} ${s.text}`}>
                        <Icon className="w-2.5 h-2.5 inline mr-0.5" />
                        {s.label}
                      </span>
                      <span className="text-[11px] text-gray-500">{(d.items || []).length} line{(d.items || []).length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="text-xs font-bold text-gray-900 mt-0.5" data-testid={`imp-draft-total-${d.id}`}>
                      {d.confirmed_total != null ? <><span className="text-emerald-700">£{Number(d.confirmed_total).toFixed(2)}</span> <span className="text-[9px] font-bold uppercase text-emerald-700">confirmed</span></> : <>£{(d.subtotal || 0).toFixed(2)} <span className="text-[9px] font-bold uppercase text-amber-700">est.</span></>}
                    </div>
                    {d.status !== 'draft' && (
                      <span role="link" tabIndex={0} onClick={(e) => { e.stopPropagation(); navigate(`/material-orders/${d.id}`); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(`/material-orders/${d.id}`); } }}
                        className="block mt-1 text-[11px] font-semibold text-emerald-700 underline" data-testid={`imp-view-order-${d.id}`}>View order & stages →</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tier banner */}
        {!locked && !loading && (
          <Card className="p-3 mb-4 bg-emerald-50 border-emerald-200" data-testid="imp-tier-banner">
            <div className="text-sm text-emerald-900 flex items-center gap-2 flex-wrap">
              <CheckCircle className="w-4 h-4" />
              {tier.assigned ? (
                <span>You&apos;re on the <strong>{tierLabel}</strong> tier for {brandConf?.label}. Prices below are what Ox will charge you.</span>
              ) : (
                <span>No material-cost tier assigned for {brandConf?.label} — using default. Contact Ox if pricing looks off.</span>
              )}
            </div>
          </Card>
        )}

        {/* Catalogue */}
        {!locked && (
          loading ? (
            <Card className="p-10 text-center text-sm text-gray-500">Loading prices…</Card>
          ) : products.length === 0 ? (
            <Card className="p-10 text-center">
              <p className="text-sm text-gray-500 italic">No {brandConf?.label} products published yet.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.keys(grouped).map((cat) => (
                <Card key={cat} className="p-3 sm:p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{cat}</h3>
                  {/* Desktop / wide: grid table.
                      Order-builder view — price columns intentionally omitted
                      because prices live on /staff/pricing-tiers (single source
                      of truth). Cart at the bottom of the page still totals
                      the order so the installer knows what they're spending. */}
                  <div className="hidden md:grid md:grid-cols-[1fr_140px] gap-x-3 gap-y-1 text-sm items-center">
                    <div className="text-[11px] font-bold uppercase text-gray-400 tracking-wider">Product</div>
                    <div className="text-[11px] font-bold uppercase text-gray-400 tracking-wider text-right">Quantity</div>
                    {grouped[cat].map((p) => {
                      const disabled = p.price == null || isReadOnly;
                      return (
                        <React.Fragment key={p.id}>
                          <div className="text-gray-900 py-1.5 border-t border-gray-100" data-testid={`imp-row-${p.id}`}>
                            {p.name}
                            {p.unit && <span className="text-[11px] text-gray-400 ml-1.5">/ {p.unit}</span>}
                          </div>
                          <div className="py-1.5 border-t border-gray-100 text-right">
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={qtys[p.id] || ''}
                              onChange={(e) => setQty(p.id, e.target.value)}
                              disabled={disabled}
                              placeholder={disabled ? '—' : '0'}
                              data-testid={`imp-qty-${p.id}`}
                              className="h-8 text-sm text-right ml-auto w-28"
                            />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {/* Mobile: stacked cards — order-builder only (no price
                      display; see /staff/pricing-tiers for the price list). */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {grouped[cat].map((p) => {
                      const disabled = p.price == null || isReadOnly;
                      return (
                        <div key={p.id} className="py-2.5" data-testid={`imp-row-mobile-${p.id}`}>
                          <div className="text-sm font-medium text-gray-900">
                            {p.name}
                            {p.unit && <span className="text-[11px] text-gray-400 ml-1.5">/ {p.unit}</span>}
                          </div>
                          {p.price == null && (
                            <div className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Ask Ox to set your tier price
                            </div>
                          )}
                          <div className="mt-1.5 flex items-center gap-2">
                            <span className="text-[11px] text-gray-500">Qty:</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={qtys[p.id] || ''}
                              onChange={(e) => setQty(p.id, e.target.value)}
                              disabled={disabled}
                              placeholder={disabled ? '—' : '0'}
                              data-testid={`imp-qty-mobile-${p.id}`}
                              className="h-8 text-sm text-right w-24"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}
            </div>
          )
        )}
      </div>

      {/* Floating cart panel — only appears when there's something to quote */}
      {!locked && cartActive && (
        <CartPanel
          brandConf={brandConf}
          lines={lineTotals.lines}
          subtotal={lineTotals.subtotal}
          notes={notes}
          setNotes={setNotes}
          customerRef={customerRef}
          setCustomerRef={setCustomerRef}
          isReadOnly={isReadOnly}
          orderId={orderId}
          orderStatus={orderStatus}
          dirty={dirty}
          saving={saving}
          hasMissingPrice={hasMissingPrice}
          onSave={save}
          onSubmit={submit}
          onDelete={deleteDraft}
          onClear={newQuote}
          onRemoveLine={(pid) => setQty(pid, '')}
          isAmbassador={isAmbassador}
          creditBalance={creditBalance}
          creditToApply={creditToApply}
          voucher={voucher}
          setVoucher={setVoucher}
          setCreditToApply={setCreditToApply}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.draft;
  const Icon = s.icon;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${s.bg} ${s.text}`}>
      <Icon className="w-3 h-3 inline mr-1" />
      {s.label}
    </span>
  );
}

function CartPanel({
  brandConf, lines, subtotal, notes, setNotes, customerRef, setCustomerRef,
  isReadOnly, orderId, orderStatus, dirty, saving, hasMissingPrice,
  onSave, onSubmit, onDelete, onClear, onRemoveLine,
  isAmbassador, creditBalance, creditToApply, setCreditToApply, voucher, setVoucher,
}) {
  const [collapsed, setCollapsed] = useState(false);
  // Net amount the installer actually owes after credit, previewed live in
  // the cart. Credit is capped client-side at min(balance, subtotal); the
  // server enforces the same cap plus balance validation on submit.
  const creditCap = Math.min(creditBalance || 0, subtotal || 0);
  const creditNum = parseFloat(creditToApply || '0') || 0;
  const creditApplied = Math.max(0, Math.min(creditNum, creditCap));
  const voucherApplied = Math.min(voucher?.value || 0, Math.max(0, subtotal - creditApplied));
  const netTotal = Math.max(0, subtotal - creditApplied - voucherApplied);
  const showCreditPanel = isAmbassador && !isReadOnly && (creditBalance || 0) > 0 && subtotal > 0;
  return (
    <div
      className="fixed bottom-0 right-0 left-0 sm:left-auto sm:right-4 sm:bottom-4 z-40 sm:w-[380px]"
      data-testid="imp-cart"
    >
      <Card className="rounded-none sm:rounded-xl shadow-2xl border-2 overflow-hidden" style={{ borderColor: brandConf?.color }}>
        {/* Header */}
        <div
          className="px-3 py-2 flex items-center gap-2 cursor-pointer select-none"
          style={{ backgroundColor: brandConf?.color, color: brandConf?.dark ? '#0B0B0E' : '#fff' }}
          onClick={() => setCollapsed((c) => !c)}
          data-testid="imp-cart-toggle"
        >
          <ShoppingCart className="w-4 h-4" />
          <div className="text-sm font-bold flex-1">
            {orderId && orderStatus === 'submitted'
              ? `${brandConf?.label} order`
              : orderId
                ? `${brandConf?.label} draft`
                : `${brandConf?.label} quote`}
          </div>
          <span className="text-xs font-bold">£{subtotal.toFixed(2)}</span>
          <span className="text-[10px] opacity-80 ml-1">{lines.length} line{lines.length === 1 ? '' : 's'}</span>
        </div>

        {!collapsed && (
          <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
            {/* Status banner */}
            {orderId && (
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill status={orderStatus} />
                {dirty && !isReadOnly && (
                  <span className="text-[10px] font-bold text-amber-700 uppercase">Unsaved changes</span>
                )}
              </div>
            )}

            {/* Lines */}
            <div className="space-y-1">
              {lines.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-xs" data-testid={`imp-cart-line-${l.id}`}>
                  <span className="flex-1 truncate text-gray-700">
                    {l.name}
                    <span className="text-gray-400"> × {l.qty}</span>
                  </span>
                  <span className="font-bold text-gray-900 shrink-0">£{l.line.toFixed(2)}</span>
                  {!isReadOnly && (
                    <button
                      onClick={() => onRemoveLine(l.id)}
                      className="text-gray-400 hover:text-red-600 shrink-0"
                      data-testid={`imp-cart-remove-${l.id}`}
                      aria-label="Remove line"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {lines.length === 0 && (
                <p className="text-xs text-gray-400 italic text-center py-2">No items yet — enter a quantity above.</p>
              )}
            </div>

            {!isReadOnly && (
              <VoucherRail brand={brandConf?.key || 'probound'} items={lines.map((l) => ({ name: l.name, qty: l.qty }))} selectedId={voucher?.id} onSelect={(id, value) => setVoucher({ id, value })} accent={brandConf?.color || '#2F5E96'} compact />
            )}
            <div className="border-t border-gray-100 pt-2 flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase font-bold tracking-wider">Estimated subtotal</span>
              <span className="text-base font-bold text-gray-900" data-testid="imp-cart-subtotal">£{subtotal.toFixed(2)}</span>
            </div>
            <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="imp-estimate-notice">
              <strong>Estimate only — please don't pay yet.</strong> Ox Resin will check the order and confirm the exact invoice amount before payment.
            </p>
            {voucherApplied > 0 && (
              <div className="flex items-center justify-between" data-testid="imp-voucher-line">
                <span className="text-[11px] font-semibold text-amber-800">Training voucher − £{voucherApplied.toFixed(2)}</span>
                <span className="text-sm font-bold text-gray-900" data-testid="imp-voucher-net-total">To pay £{netTotal.toFixed(2)}</span>
              </div>
            )}

            {/* Ambassador credit — only shown when the installer is dual-role
                and has a positive balance. Client-side cap only; server
                re-validates on submit. */}
            {showCreditPanel && (
              <div
                className="rounded-md border border-emerald-200 bg-emerald-50 p-2 space-y-1.5"
                data-testid="imp-credit-panel"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-emerald-900 uppercase tracking-wider">
                    Ambassador Credit
                  </span>
                  <span className="text-[11px] text-emerald-900" data-testid="imp-credit-balance">
                    Balance £{creditBalance.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    max={creditCap}
                    value={creditToApply}
                    onChange={(e) => setCreditToApply(e.target.value)}
                    placeholder="0.00"
                    data-testid="imp-credit-input"
                    className="h-8 text-xs flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => setCreditToApply(creditCap.toFixed(2))}
                    className="text-[10px] font-semibold px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="imp-credit-apply-max"
                  >
                    Apply max
                  </button>
                  {creditToApply && (
                    <button
                      type="button"
                      onClick={() => setCreditToApply('')}
                      className="text-[10px] text-emerald-800 hover:text-emerald-950"
                      data-testid="imp-credit-clear"
                      aria-label="Clear credit"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {creditApplied > 0 && (
                  <div
                    className="flex items-center justify-between pt-1 border-t border-emerald-200"
                    data-testid="imp-credit-preview"
                  >
                    <span className="text-[11px] text-emerald-900">
                      Credit − £{creditApplied.toFixed(2)}
                    </span>
                    <span className="text-sm font-bold text-emerald-900" data-testid="imp-credit-net-total">
                      Net £{netTotal.toFixed(2)}
                    </span>
                  </div>
                )}
                <p className="text-[10px] text-emerald-700 italic">
                  Applied against the Xero invoice when this order is billed.
                </p>
              </div>
            )}

            {/* Customer ref + notes */}
            {!isReadOnly && (
              <div className="space-y-1.5 pt-1">
                <Input
                  value={customerRef}
                  onChange={(e) => setCustomerRef(e.target.value)}
                  placeholder="Customer / job reference (optional)"
                  data-testid="imp-cart-ref"
                  className="h-8 text-xs"
                />
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes for Ox (optional)"
                  data-testid="imp-cart-notes"
                  className="h-8 text-xs"
                />
              </div>
            )}
            {isReadOnly && (customerRef || notes) && (
              <div className="text-xs text-gray-500 space-y-0.5 pt-1">
                {customerRef && <div><strong>Ref:</strong> {customerRef}</div>}
                {notes && <div><strong>Notes:</strong> {notes}</div>}
              </div>
            )}

            {/* Actions */}
            {!isReadOnly ? (
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onSave}
                  disabled={saving || lines.length === 0 || !dirty}
                  data-testid="imp-cart-save"
                  className="flex-1"
                >
                  <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save draft'}
                </Button>
                <Button
                  size="sm"
                  onClick={onSubmit}
                  disabled={saving || lines.length === 0 || hasMissingPrice}
                  data-testid="imp-cart-submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Send className="w-3.5 h-3.5 mr-1" /> Submit
                </Button>
              </div>
            ) : (
              <div className="text-center text-xs text-emerald-700 bg-emerald-50 rounded p-2 flex items-center justify-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" /> Submitted to Ox — cannot be edited.
              </div>
            )}

            {hasMissingPrice && !isReadOnly && (
              <p className="text-[11px] text-amber-700 text-center">
                <AlertCircle className="w-3 h-3 inline mr-0.5" /> Remove &ldquo;Ask Ox&rdquo; lines before submitting.
              </p>
            )}

            {/* Secondary actions */}
            <div className="flex gap-2 justify-between pt-1 border-t border-gray-50">
              <button onClick={onClear} className="text-[11px] text-gray-500 hover:text-gray-900" data-testid="imp-cart-clear">
                <Plus className="w-3 h-3 inline mr-0.5" /> New quote
              </button>
              {orderId && orderStatus === 'draft' && (
                <button onClick={onDelete} className="text-[11px] text-red-600 hover:text-red-800" data-testid="imp-cart-delete">
                  <Trash2 className="w-3 h-3 inline mr-0.5" /> Delete draft
                </button>
              )}
            </div>

            <p className="text-[10px] text-gray-400 italic text-center pt-1">
              <Clock className="w-2.5 h-2.5 inline mr-0.5" /> Drafts only go to Ox once you hit <strong>Submit</strong>.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

function Header() {
  const navigate = useNavigate();
  return (
    <div
      className="text-white"
      style={{
        background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)',
        borderBottom: '3px solid #D4A24B',
      }}
    >
      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="imp-back"
          className="p-2 hover:bg-white/10 rounded-lg shrink-0"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
            <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4A24B' }} />
            New Materials Order
          </h1>
          <p className="hidden sm:block text-white/70 text-[11px] mt-0.5">
            Type quantities → save draft or submit to Ox. See the price list on{' '}
            <a href="/staff/pricing-tiers" className="underline hover:text-white">Product Prices</a>.
          </p>
        </div>
        <Button
          onClick={() => navigate('/')}
          variant="outline"
          size="sm"
          className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          data-testid="imp-home"
        >
          <Home className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Dashboard</span>
        </Button>
      </div>
    </div>
  );
}
